import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B', purple:'#7C3AED' };
const TEST_ICONS = { ielts:'🎓', cefr:'📊', sat:'📐', presidential:'🏛️' };
const TEST_COLORS = { ielts: C.teal, cefr: C.purple, sat: C.amber, presidential: C.success };

const STATUS_CONFIG = {
  booked:     { label:'Booked — Awaiting Payment', color:'#92400E', bg:'#FEF3C7' },
  activated:  { label:'Ready to Start', color:'#065F46', bg:'#D1FAE5' },
  completed:  { label:'Completed', color: C.grey, bg:'#F3F4F6' },
  no_show:    { label:'No Show', color: C.error, bg:'#FEE2E2' },
};

const PAY_STATUS = {
  pending: { label:'Payment Pending', color: C.amber, bg:'#FEF3C7' },
  paid:    { label:'Paid', color: C.success, bg:'#D1FAE5' },
  refunded:{ label:'Refunded', color: C.grey, bg:'#F3F4F6' },
};

export default function StudentMyTests() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const downloadCert = async (code) => {
    try {
      const r = await api.get(`/api/certificates/${code}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {}
  };

  useEffect(() => {
    api.get('/api/student/mock-tests/my-tests')
      .then(r => setBookings(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: 40, textAlign:'center', color: C.grey }}>Loading your tests...</div>
  );

  if (bookings.length === 0) return (
    <div style={{ padding: 40, textAlign:'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>📝</div>
      <div style={{ color: C.navy, fontWeight: 700, fontSize: 17, marginBottom: 6 }}>No Mock Tests Yet</div>
      <div style={{ color: C.grey, fontSize: 14 }}>Visit your school's website to book a mock test.</div>
    </div>
  );

  return (
    <div style={{ padding: '24px 0' }}>
      <h3 style={{ margin:'0 0 20px', color: C.navy, fontSize: 18, fontWeight: 800 }}>🧪 My Mock Tests</h3>
      <div style={{ display:'flex', flexDirection:'column', gap: 16 }}>
        {bookings.map(b => {
          const slug = b.test_type?.slug || '';
          const color = TEST_COLORS[slug] || C.teal;
          const sess = b.session;
          const testStatus = STATUS_CONFIG[b.test_status] || STATUS_CONFIG.booked;
          const payStatus = PAY_STATUS[b.payment_status] || PAY_STATUS.pending;
          const canStart = sess && (sess.status === 'not_started' || sess.status === 'released');
          const canResume = sess && sess.status === 'in_progress';
          const isDone = sess && ['submitted','marked'].includes(sess.status);

          return (
            <div key={b.id} style={{ background:'#fff', borderRadius: 14, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 10px rgba(0,0,0,0.04)' }}>
              {/* Color stripe */}
              <div style={{ height: 5, background: color }} />
              <div style={{ padding: 20 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 24 }}>{TEST_ICONS[slug] || '📝'}</span>
                      <div>
                        <div style={{ fontWeight: 800, color: C.navy, fontSize: 16 }}>{b.test_type?.name || 'Mock Test'}</div>
                        {b.slot && (
                          <div style={{ fontSize: 12, color: C.grey, marginTop: 2 }}>
                            📅 {new Date(b.slot.slot_date + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'long' })} at {b.slot.slot_time}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap: 8, marginTop: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding:'3px 10px', borderRadius: 20, background: payStatus.bg, color: payStatus.color }}>
                        {payStatus.label}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding:'3px 10px', borderRadius: 20, background: testStatus.bg, color: testStatus.color }}>
                        {testStatus.label}
                      </span>
                      {sess?.flagged && (
                        <span style={{ fontSize: 11, fontWeight: 700, padding:'3px 10px', borderRadius: 20, background:'#FEE2E2', color: C.error }}>⚠️ Flagged</span>
                      )}
                    </div>
                  </div>

                  {/* Score (if done) */}
                  {isDone && sess && (
                    (slug === 'ielts' && sess.status !== 'marked') ? (
                      <div style={{ textAlign:'center', padding:'10px 16px', background:'#FEF3C7', border:'1px solid #FCD34D', borderRadius: 12, minWidth: 120 }}>
                        <div style={{ fontSize: 22 }}>⏳</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color:'#92400E' }}>Awaiting tutor marking</div>
                      </div>
                    ) : (
                    <div style={{ textAlign:'center', padding:'10px 20px', background: C.bg, borderRadius: 12, minWidth: 100 }}>
                      <div style={{ fontSize: 26, fontWeight: 900, color }}>
                        {slug === 'ielts' ? sess.band_score : slug === 'sat' ? sess.band_score : `${Math.round(sess.total_score || 0)}%`}
                      </div>
                      <div style={{ fontSize: 11, color: C.grey }}>{slug === 'ielts' ? 'Band Score' : slug === 'sat' ? 'SAT Score' : 'Score'}</div>
                      {sess.cefr_result && slug === 'cefr' && <div style={{ fontSize: 12, fontWeight: 700, color: C.purple, marginTop: 2 }}>{sess.cefr_result}</div>}
                    </div>
                    )
                  )}
                </div>

                {/* Section scores (if done) */}
                {isDone && sess?.section_scores && Object.keys(sess.section_scores).length > 0 && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap: 8, marginTop: 14 }}>
                    {Object.entries(sess.section_scores).map(([sec, data]) => {
                      const isBand = data && data.band != null;
                      const pct = isBand ? (Number(data.band) / 9) * 100 : (data && data.percent != null ? data.percent : 0);
                      const col = pct >= 70 ? C.success : pct >= 50 ? C.amber : C.error;
                      return (
                        <div key={sec} style={{ background: C.bg, borderRadius: 8, padding:'6px 12px', fontSize: 12 }}>
                          <span style={{ color: C.grey, textTransform:'capitalize' }}>{sec.replace(/_/g,' ')}: </span>
                          <span style={{ fontWeight: 700, color: col }}>{isBand ? `Band ${Number(data.band).toFixed(1)}` : `${data.percent}%`}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Status messages */}
                {b.test_status === 'booked' && b.payment_status === 'pending' && (
                  <div style={{ marginTop: 14, padding:'10px 14px', background:'#FEF3C7', borderRadius: 10, fontSize: 13, color:'#92400E' }}>
                    💰 Please visit the Talk2Talk centre to pay. Your test will be activated after payment confirmation.
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display:'flex', gap: 10, marginTop: 16 }}>
                  {canStart && (
                    <button onClick={() => navigate(`/mock-test/${sess.id}`)} style={{ padding:'10px 24px', borderRadius: 10, background: C.success, color:'#fff', border:'none', cursor:'pointer', fontSize: 14, fontWeight: 700 }}>
                      🚀 Start Test Now
                    </button>
                  )}
                  {canResume && (
                    <button onClick={() => navigate(`/mock-test/${sess.id}`)} style={{ padding:'10px 24px', borderRadius: 10, background: C.amber, color:'#fff', border:'none', cursor:'pointer', fontSize: 14, fontWeight: 700 }}>
                      ▶ Resume Test
                    </button>
                  )}
                  {isDone && sess && (
                    <button onClick={() => navigate(`/mock-test/${sess.id}`)} style={{ padding:'10px 24px', borderRadius: 10, background: C.teal, color:'#fff', border:'none', cursor:'pointer', fontSize: 14, fontWeight: 700 }}>
                      📊 View Results
                    </button>
                  )}
                  {isDone && sess?.certificate?.verification_code && (
                    <button onClick={() => downloadCert(sess.certificate.verification_code)} style={{ padding:'10px 24px', borderRadius: 10, background:'#fff', color: C.teal, border:`2px solid ${C.teal}`, cursor:'pointer', fontSize: 14, fontWeight: 700 }}>
                      🏆 Certificate
                    </button>
                  )}
                  {sess?.status === 'submitted' && (
                    <span style={{ padding:'10px 14px', fontSize: 13, color: C.amber, fontWeight: 600 }}>
                      ⏳ Awaiting final marking
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
